import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  checkAdminStatus: () => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const checkAdminStatus = async () => {
    if (!user?.email) {
      setIsAdmin(false);
      return;
    }

    try {
      console.log('🔍 Verificando status de admin para:', user.email);
      
      // Lista de emails admin (temporário - você pode mover isso para variável de ambiente)
      const adminEmails = ['rodrigo.roh@yahoo.com'];
      
      if (adminEmails.includes(user.email.toLowerCase())) {
        console.log('✅ Usuário é ADMIN!');
        setIsAdmin(true);
      } else {
        console.log('❌ Usuário NÃO é admin');
        setIsAdmin(false);
      }

    } catch (error) {
      console.error('❌ Erro ao verificar admin:', error);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    // Verificar sessão inicial
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.warn('Erro ao recuperar sessão:', error);
          setSession(null);
          setUser(null);
          setIsAdmin(false);
        } else if (session) {
          setSession(session);
          setUser(session.user);
        }
      } catch (err) {
        console.warn('Erro ao inicializar autenticação:', err);
        setSession(null);
        setUser(null);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Escutar mudanças de autenticação
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (loading) {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Verificar status de admin quando o usuário mudar
  useEffect(() => {
    if (user) {
      checkAdminStatus();
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        // Criar perfil do usuário
        const { error: profileError } = await supabase.from('profiles').upsert({
          id: data.user.id,
          email,
          full_name: fullName,
        }, {
          onConflict: 'id'
        });

        if (profileError) {
          console.warn('Erro ao criar perfil:', profileError);
        }
      }
    } catch (error) {
      console.error('Erro no cadastro:', error);
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      
      if (data.session) {
        setSession(data.session);
        setUser(data.user);
      }
    } catch (error) {
      console.error('Erro no login:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setSession(null);
      setUser(null);
      setIsAdmin(false);
    } catch (error) {
      console.error('Erro ao sair:', error);
      setSession(null);
      setUser(null);
      setIsAdmin(false);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, checkAdminStatus, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}
